import { rollAspectsForRarity } from '../utils/aspectUtils';

// Type definitions for module data
export interface ModuleDefinition {
    id: string;
    dataId?: number; // Numeric ID for encoding (1-99)
    name: string;
    symbolId: string;
    slotType: 'weapon' | 'support';
    tier: number;
    rarity: 'common' | 'rare' | 'officer';
    cmpCost: number;
    rctCost: number;
    scaling?: {
        baseDamage: number;
        damagePerLevel: number;
    };
    passiveEffects?: Array<{
        effectId: string;
        params: Record<string, unknown>;
    }>;
    skill?: {
        chargeCost: number;
        chargePerOrb: number;
        castEffects: Array<{
            effectId: string;
            params: Record<string, unknown>;
        }>;
    };
}

export interface ShipDefinition {
    id: string;
    name: string;
    cmpBudget: number;
    rctBudget: number;
    slots: {
        weapon: number;
        support: number;
    };
    baseStats: {
        shieldMax: number;
        resists: Record<string, number>;
        baseMoveTimeSeconds: number;
    };
}

export interface SymbolDraw {
    type: 'line' | 'circle' | 'rect' | 'arc' | 'polygon';
    x?: number;
    y?: number;
    x1?: number;
    y1?: number;
    x2?: number;
    y2?: number;
    w?: number;
    h?: number;
    radius?: number;
    width?: number;
    start?: number;
    end?: number;
    points?: number[];
    fill?: boolean;
    stroke?: boolean;
}

export interface SymbolDefinition {
    category: string;
    description: string;
    draws: SymbolDraw[];
}

export interface SymbolsData {
    version: string;
    symbols: Record<string, SymbolDefinition>;
    rarityColors: Record<string, string>;
    affinityColors: Record<string, string>;
}

export interface EvolutionRecipe {
    fromModuleId: string;
    toModuleId: string;
    requiresBlueprintId: string;
    requiresLevel: number;
    consumeDuplicates: number;
}

export interface EffectDefinition {
    id: string;
    type: string;
    trigger: string;
    stacking?: string;
    paramsSchema: Record<string, string>;
}

export interface EffectsData {
    version: string;
    effects: EffectDefinition[];
}

export interface ModulesData {
    version: string;
    ship: ShipDefinition;
    modules: ModuleDefinition[];
    evolutionRecipes: EvolutionRecipe[];
}

export interface PlayerModule {
    definitionId: string;
    instanceId: string;
    level: number;
    aspects: string[];  // Rolled aspect elements (e.g., ['THERMAL'], ['CRYO', 'EMP'])
}

// Load and cache the data
let modulesData: ModulesData | null = null;
let symbolsData: SymbolsData | null = null;
let effectsData: EffectsData | null = null;

export async function loadModuleData(): Promise<ModulesData> {
    if (modulesData) return modulesData;

    const response = await fetch('/data/modules.json');
    modulesData = await response.json();
    return modulesData!;
}

export async function loadSymbolsData(): Promise<SymbolsData> {
    if (symbolsData) return symbolsData;

    const response = await fetch('/data/symbols.json');
    symbolsData = await response.json();
    return symbolsData!;
}

export async function loadEffectsData(): Promise<EffectsData> {
    if (effectsData) return effectsData;

    const response = await fetch('/data/effects.json');
    effectsData = await response.json();
    return effectsData!;
}

export function getModulesData(): ModulesData | null {
    return modulesData;
}

export function getSymbolsData(): SymbolsData | null {
    return symbolsData;
}

export function getEffectsData(): EffectsData | null {
    return effectsData;
}

export function getEffect(id: string): EffectDefinition | undefined {
    return effectsData?.effects.find(e => e.id === id);
}

export function getModuleDefinition(id: string): ModuleDefinition | undefined {
    return modulesData?.modules.find(m => m.id === id);
}

export function getSymbol(id: string): SymbolDefinition | undefined {
    return symbolsData?.symbols[id];
}

export function getEvolutionRecipe(moduleId: string): EvolutionRecipe | undefined {
    return modulesData?.evolutionRecipes.find(r => r.fromModuleId === moduleId);
}

// Get all common modules for guest mode / starter inventory
export function getCommonModules(): PlayerModule[] {
    if (!modulesData) return [];
    return modulesData.modules
        .filter(m => m.rarity === 'common')
        .map((m, index) => ({
            definitionId: m.id,
            instanceId: `starter_${index}`,
            level: 1,
            aspects: rollAspectsForRarity('common'),
        }));
}
